import { Text } from "@ui-kitten/components";
import Dialog from "components/Dialog";
import { EventAvailabilityDetails, SubscriptionDetails } from "features/types";
import { requestDeferredBookingRequest } from "queries/bookingRequest.queries";
import React, { useState } from "react";
import { View } from "react-native";
import { getRemainingAvailableSlotsForComponent } from "screens/Reserve/helpers";
import SeatQuantitySelector from "screens/SeatSelection/components/SeatQuantitySelector";
import toast from "utils/toast";

interface RSVPDialogProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  event: EventAvailabilityDetails;
  planPackageId: number;
  selectedSubscription: SubscriptionDetails;
}

const RSVPDialog: React.FC<RSVPDialogProps> = ({
  visible,
  setVisible,
  event,
  planPackageId,
  selectedSubscription,
}) => {
  const maxQuantity = getRemainingAvailableSlotsForComponent({
    component: selectedSubscription.planPackage.primaryComponent,
    subscriptions: [selectedSubscription],
    eventId: event.eventId,
    events: [],
    isChangingSeats: false,
    maxSeatsAllowed: event.numAvailable,
  });

  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const primaryComponentId =
        selectedSubscription.planPackage.primaryComponent.id;

      const items = Array.from({ length: quantity }, () => ({
        planPackageComponentId: primaryComponentId,
      }));

      const bookingRequest = await requestDeferredBookingRequest({
        eventId: event.eventId,
        planPackageId,
        items,
        quantity,
        complimentary: false,
      });
      toast.success(
        bookingRequest.status === "WAITLISTED"
          ? "Your request is on the waitlist. You will be notified if a spot becomes available."
          : "Your RSVP has been submitted!",
      );
      setVisible(false);
      setQuantity(1);
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to submit RSVP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      setVisible={setVisible}
      cancelText="Cancel"
      onCancel={() => {
        setVisible(false);
        setQuantity(1);
      }}
      okText="Submit"
      onOk={handleSubmit}
      loading={loading}
    >
      <Text category="h6" style={{ marginBottom: 16 }}>
        RSVP to {event.eventName}
      </Text>
      <Text category="s1" style={{ marginBottom: 12 }}>
        How many seats?
      </Text>
      <View style={{ alignItems: "center" }}>
        <SeatQuantitySelector
          size={maxQuantity}
          selectedPartySize={quantity}
          onChange={setQuantity}
          variant="ticker"
          eventType={event.eventType}
        />
      </View>
    </Dialog>
  );
};

export default RSVPDialog;
